import { useState } from "react";

import { CoiReviewForm } from "./CoiReviewForm";
import { CoiUploader } from "./CoiUploader";
import { useCoiUpload, type CoiDocument } from "./api";

// Phase 2b coi-upload-and-review — `/account/coi` page. State machine
// (mirrors server status):
//   nothing uploaded      → <CoiUploader/>
//   processing            → spinner + "Reading your certificate…"
//   awaiting_customer_review → <CoiReviewForm/>
//   awaiting_admin_review | approved | rejected | expired | ocr_failed
//                          → status banner + (where applicable) "upload again"

export function CoiPage(): JSX.Element {
  const [activeId, setActiveId] = useState<number | null>(null);
  const { data, isLoading } = useCoiUpload(activeId);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Certificate of Insurance</h1>
        <p className="mt-1 text-sm text-zinc-600">
          We need a current ACORD 25 (or equivalent) on file before we can
          deliver equipment. Most rentals reuse the same certificate
          until its expiry date.
        </p>
      </header>

      {activeId === null && <CoiUploader onUploaded={setActiveId} />}

      {activeId !== null && isLoading && <ProcessingPanel />}

      {activeId !== null && data && <StatusPanel doc={data} reset={() => setActiveId(null)} />}
    </section>
  );
}

function ProcessingPanel(): JSX.Element {
  return (
    <div role="status" className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
      Loading your certificate…
    </div>
  );
}

function StatusPanel({ doc, reset }: { doc: CoiDocument; reset: () => void }): JSX.Element {
  if (doc.status === "processing") {
    return (
      <div role="status" className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
        Reading your certificate… this usually takes a few seconds.
      </div>
    );
  }
  if (doc.status === "awaiting_customer_review") {
    return <CoiReviewForm doc={doc} onSubmitted={reset} />;
  }
  if (doc.status === "awaiting_admin_review") {
    return (
      <InfoPanel
        title="We received your certificate"
        body="Our team will review it shortly and email you when it's approved (or if anything else is needed)."
      />
    );
  }
  if (doc.status === "approved") {
    return (
      <InfoPanel
        title="Approved"
        body="Your certificate is on file and good through its expiry date. You don't need to do anything further."
      />
    );
  }
  if (doc.status === "rejected" || doc.status === "ocr_failed") {
    return (
      <div className="space-y-3">
        <InfoPanel
          title={doc.status === "rejected" ? "Not approved" : "We couldn't read your certificate"}
          body="Please upload a new certificate."
          tone="danger"
        />
        <CoiUploader onUploaded={() => undefined} />
      </div>
    );
  }
  if (doc.status === "expired") {
    return (
      <InfoPanel
        title="Expired"
        body="The certificate on file has expired. Please upload a current one."
        tone="danger"
      />
    );
  }
  return <pre className="text-xs text-zinc-500">{JSON.stringify(doc, null, 2)}</pre>;
}

function InfoPanel({
  title,
  body,
  tone = "default"
}: {
  title: string;
  body: string;
  tone?: "default" | "danger";
}): JSX.Element {
  const cls =
    tone === "danger"
      ? "rounded-md border border-danger/30 bg-danger/5 p-4 text-sm text-danger"
      : "rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700";
  return (
    <div className={cls}>
      <p className="font-medium">{title}</p>
      <p className="mt-1">{body}</p>
    </div>
  );
}
